import React from 'react';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Divider from '@mui/material/Divider';
import TextField from '@mui/material/TextField';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAxiosGet } from '../Hooks/HttpRequests';
import MuiTable from '../Components/MuiTable';
// If this filename is actually `ChordTest.jsx`, make sure the casing matches:
import ChordTest from './Chordtest';
import { PastDrafts } from './Draft';

// ────────────────────────────────────────────────────────────────────────────────
// Top-level: just render the tabs. Let each tab component load its own data.
// ────────────────────────────────────────────────────────────────────────────────
function Trades() {
  const navigate = useNavigate();
  const location = useLocation();

  // Map internal tab keys <-> route slugs
  const slugFromTab = {
    ledger: 'tradeledger',
    team: 'byteam',
    partner: 'bypartner',
    picks: 'draftpicktrades',
    pastdrafts: 'pasttrades', // requested slug name
    last: 'lasttrades',
    most: 'mosttradedplayers',
    all: 'alltrades',
    test: 'test',
  };
  const tabFromSlug = Object.fromEntries(Object.entries(slugFromTab).map(([k, v]) => [v, k]));

  const segments = location.pathname.split('/').filter(Boolean);
  const slug = segments[1] || '';
  const currentTab = tabFromSlug[slug] || 'ledger';

  // Redirect bare /trades to default tab route
  React.useEffect(() => {
    if (segments[0] === 'trades' && !segments[1]) {
      navigate(`/trades/${slugFromTab[currentTab]}`, { replace: true });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const handleChange = (_event, newValue) => {
    const nextSlug = slugFromTab[newValue] || 'tradeledger';
    if (nextSlug !== slug) navigate(`/trades/${nextSlug}`);
  };

  return (
    <div className="h-2/4">
      <Tabs
        value={currentTab}
        onChange={handleChange}
        variant="scrollable"
        scrollButtons="auto"
        aria-label="Trades tabs"
        textColor="primary"
      >
        {/* <Tab value="all" label="All Trades" /> */}
        <Tab value="ledger" label="Trade Ledger" />
        <Tab value="team" label="By Team" />
        <Tab value="partner" label="By Partner" />
        <Tab value="picks" label="Draft Pick Trades" />
        <Tab value="pastdrafts" label="Past Drafts" />
        <Tab value="last" label="Last Trades" />
        {/* <Tab value="test" label="Test" /> */}
        <Tab value="most" label="Most Traded Players" />
      </Tabs>
      <Divider />

      <div className="h-2/4">
        {currentTab === 'all' && <AllTrades />}
        {currentTab === 'team' && <ByTeam />}
        {currentTab === 'partner' && <ByPartner />}
        {currentTab === 'picks' && <PickTrades />}
        {currentTab === 'last' && <Last />}
        {currentTab === 'ledger' && <Ledger />}
        {currentTab === 'pastdrafts' && <PastDrafts />}
        {currentTab === 'test' && <TestTrades />}
        {currentTab === 'most' && <Most />}
      </div>
    </div>
  );
}

export default Trades;

// ────────────────────────────────────────────────────────────────────────────────
// Tabs
// ────────────────────────────────────────────────────────────────────────────────

function AllTrades() {
  // Source updated: consolidated enriched trades export
  const url = 'https://supermegadynasty.com/smd_trades_enriched.json';

  const [query, setQuery] = React.useState('');

  const headCells = [
    { id: 'trade_date',          numeric: false, disablePadding: false, label: 'Date', minWidth: 90 },
    { id: 'trade_partners',      numeric: false, disablePadding: false, label: 'Partners', minWidth: 140 },
    { id: 'trade_team_a',        numeric: false, disablePadding: false, label: 'Team A', minWidth: 120 },
    { id: 'trade_team_a_assets', numeric: false, disablePadding: false, label: 'Team A Adds', minWidth: 260 },
    { id: 'trade_team_b',        numeric: false, disablePadding: false, label: 'Team B', minWidth: 120 },
    { id: 'trade_team_b_assets', numeric: false, disablePadding: false, label: 'Team B Adds', minWidth: 260 },
  ];

  const rowFilter = React.useCallback((row) => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return true;
    const fields = [
      row.trade_partners,
      row.trade_team_a,
      row.trade_team_b,
      row.trade_team_a_assets,
      row.trade_team_b_assets,
      row.picks_to_players,
    ];
    return fields.some((v) => String(v || '').toLowerCase().includes(q));
  }, [query]);

  return (
    <div className="md:flex flex-wrap max-w-prose w-full">
      <div className="w-full mb-2">
        <TextField
          size="small"
          label="Filter (partner, asset, picks)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      <MuiTable
        table="enriched_trades"
        headCells={headCells}
        url={url}
        title="All Trades (Enriched)"
        page={50}
        rowFilter={rowFilter}
      />
    </div>
  );
}

function ByTeam() {
  const url = 'https://supermegadynasty.com/smd/output/all/complete_trades.json';

  const headCells = [
    { id: 'username',    numeric: false, disablePadding: false, label: 'Team',        },
    { id: 'trades',      numeric: true,  disablePadding: false, label: 'All Trades', maxWidth: 50, minWidth: 50, width: 50 },
    { id: 'trades_2026', numeric: true,  disablePadding: false, label: '2026',       maxWidth: 50, minWidth: 50, width: 50 },
    { id: 'trades_2025', numeric: true,  disablePadding: false, label: '2025',       maxWidth: 50, minWidth: 50, width: 50 },
    { id: 'trades_2024', numeric: true,  disablePadding: false, label: '2024',       maxWidth: 50, minWidth: 50, width: 50 },
    { id: 'trades_2023', numeric: true,  disablePadding: false, label: '2023',       maxWidth: 50, minWidth: 50, width: 50 },
    { id: 'trades_2022', numeric: true,  disablePadding: false, label: '2022',       maxWidth: 50, minWidth: 50, width: 50 },
    { id: 'trades_2021', numeric: true,  disablePadding: false, label: '2021',       maxWidth: 50, minWidth: 50, width: 50 },
    { id: 'trades_2020', numeric: true,  disablePadding: false, label: '2020',       maxWidth: 50, minWidth: 50, width: 50 },
  ];

  // Let MuiTable handle the fetch/render; avoids double-fetching here.
  return (
    <div className="md:flex flex-wrap max-w-prose">
      <MuiTable headCells={headCells} url={url} title="Total Trades by Team" page={25} />
    </div>
  );
}

function ByPartner() {
  const url = 'https://supermegadynasty.com/smd_trades_by_partner.json';

  const headCells = [
    { id: 'username',      numeric: false, disablePadding: false, label: 'Team',            },
    { id: 'total',         numeric: true,  disablePadding: false, label: 'All Trades',      },
    { id: 'caseyott',      numeric: true,  disablePadding: false, label: 'caseyott',        },
    { id: 'eschnite16',    numeric: true,  disablePadding: false, label: 'eschnite16',      },
    { id: 'chasemanz',     numeric: true,  disablePadding: false, label: 'chasemanz',       },
    { id: 'milwaukeemojo', numeric: true,  disablePadding: false, label: 'milwaukeemojo',   },
    { id: 'farinamh28',    numeric: true,  disablePadding: false, label: 'farinamh28',      },
    { id: 'devanott',      numeric: true,  disablePadding: false, label: 'devanott',        },
    { id: 'beef6191',      numeric: true,  disablePadding: false, label: 'beef6191',        },
    { id: 'johnnytecmo',   numeric: true,  disablePadding: false, label: 'johnnytecmo',     },
    { id: 'mkurtz8',       numeric: true,  disablePadding: false, label: 'mkurtz8',         },
    { id: 'claytondavis',  numeric: true,  disablePadding: false, label: 'claytondavis',    },
    { id: 'schnjake',      numeric: true,  disablePadding: false, label: 'schnjake',        },
    { id: 'stacheman414',  numeric: true,  disablePadding: false, label: 'stacheman414',    },
  ];

  return (
    <div className="md:flex flex-wrap max-w-prose h-2/4">
      <MuiTable headCells={headCells} url={url} title="Total Trades by Partner" page={25} />
      <ChordTest url={url} />
      <br />
    </div>
  );
}

function PickTrades() {
  const league_id = '785959210730311680';
  const baseUrl = 'https://api.sleeper.app/v1/league/';
  const url = `${baseUrl}${league_id}/traded_picks`;

  const headCells = [
    { id: 'season',            numeric: false, disablePadding: true,  label: 'Szn' },
    { id: 'round',             numeric: true,  disablePadding: false, label: 'Rd' },
    { id: 'roster_id',         numeric: true,  disablePadding: false, label: 'Pick (Team)' },
    { id: 'previous_owner_id', numeric: true,  disablePadding: false, label: 'From' },
    { id: 'owner_id',          numeric: true,  disablePadding: false, label: 'To' },
  ];

  return (
    <div className="md:flex flex-wrap max-w-prose">
      <MuiTable table="traded_picks" headCells={headCells} url={url} title="Traded Draft Picks" page={50} />
    </div>
  );
}

function Last() {
  // Hosted export of: ~/Repos/smd/lg_optimized/smd/output/all/last_trades.json
  const url = 'https://supermegadynasty.com/smd/output/all/last_trades.json';

  const headCells = [
    { id: 'last_trade_days',   numeric: true,  disablePadding: false, label: 'Days Since' },
    { id: 'last_trade_date',   numeric: false, disablePadding: false, label: 'Date', minWidth: 100 },
    { id: 'username',          numeric: false, disablePadding: false, label: 'Team', minWidth: 100  },
    // { id: 'last_trade_type',   numeric: false, disablePadding: false, label: 'Type' },
    { id: 'last_trade_partners', numeric: false, disablePadding: false, label: 'Partners' , minWidth: 100 },
    { id: 'last_trade_adds',   numeric: false, disablePadding: false, label: 'Adds',  minWidth: 200 },
    { id: 'last_trade_drops',  numeric: false, disablePadding: false, label: 'Drops', minWidth: 200 },
    // { id: 'last_trade_picks',  numeric: false, disablePadding: false, label: 'Picks' },
    // { id: 'last_trade_waiver', numeric: true,  disablePadding: false, label: 'Waiver $' },
    // { id: 'last_trade_id',     numeric: false, disablePadding: false, label: 'Txn ID' },
  ];

  return (
    <div className="md:flex flex-wrap max-w-prose">
      <MuiTable table="last_trades" headCells={headCells} url={url} title="Last Trade by Each Team (FAAB not shown)" page={25} debugJson={false} />
    </div>
  );
}

function Ledger() {
  // All trades, normalized with partners and assets per side
  const url = 'https://supermegadynasty.com/smd_trades_enriched.json';
  const [query, setQuery] = React.useState('');

  // Past draft data (2020–2026) to align pick assets like "2023 R1 (caseyott)"
  const past2020 = useAxiosGet('https://supermegadynasty.com/smd/past_drafts/past_draft_2020.json');
  const past2021 = useAxiosGet('https://supermegadynasty.com/smd/past_drafts/past_draft_2021.json');
  const past2022 = useAxiosGet('https://supermegadynasty.com/smd/past_drafts/past_draft_2022.json');
  const past2023 = useAxiosGet('https://supermegadynasty.com/smd/past_drafts/past_draft_2023.json');
  const past2024 = useAxiosGet('https://supermegadynasty.com/smd/past_drafts/past_draft_2024.json');
  const past2025 = useAxiosGet('https://supermegadynasty.com/smd/past_drafts/past_draft_2025.json');
  const past2026 = useAxiosGet('https://supermegadynasty.com/smd/past_drafts/past_draft_2026.json');

  // Build draft pick -> player lookup for seasons <= 2026 (hooks in fixed order)
  const drafts2026 = useAxiosGet('https://api.sleeper.app/v1/league/1313578306209091584/drafts');
  const drafts2025 = useAxiosGet('https://api.sleeper.app/v1/league/1180948099673747456/drafts');
  const drafts2024 = useAxiosGet('https://api.sleeper.app/v1/league/1048282530463465472/drafts');
  const drafts2023 = useAxiosGet('https://api.sleeper.app/v1/league/920765875261632512/drafts');
  const drafts2022 = useAxiosGet('https://api.sleeper.app/v1/league/785959210730311680/drafts');
  const drafts2021 = useAxiosGet('https://api.sleeper.app/v1/league/650025951245193216/drafts');
  const drafts2020 = useAxiosGet('https://api.sleeper.app/v1/league/572103683850252288/drafts');

  // Users per season to map usernames -> roster_id
  const users2026 = useAxiosGet('https://api.sleeper.app/v1/league/1313578306209091584/users');
  const users2025 = useAxiosGet('https://api.sleeper.app/v1/league/1180948099673747456/users');
  const users2024 = useAxiosGet('https://api.sleeper.app/v1/league/1048282530463465472/users');
  const users2023 = useAxiosGet('https://api.sleeper.app/v1/league/920765875261632512/users');
  const users2022 = useAxiosGet('https://api.sleeper.app/v1/league/785959210730311680/users');
  const users2021 = useAxiosGet('https://api.sleeper.app/v1/league/650025951245193216/users');
  const users2020 = useAxiosGet('https://api.sleeper.app/v1/league/572103683850252288/users');

  // Rosters per season to map owner_id -> roster_id
  const rosters2026 = useAxiosGet('https://api.sleeper.app/v1/league/1313578306209091584/rosters');
  const rosters2025 = useAxiosGet('https://api.sleeper.app/v1/league/1180948099673747456/rosters');
  const rosters2024 = useAxiosGet('https://api.sleeper.app/v1/league/1048282530463465472/rosters');
  const rosters2023 = useAxiosGet('https://api.sleeper.app/v1/league/920765875261632512/rosters');
  const rosters2022 = useAxiosGet('https://api.sleeper.app/v1/league/785959210730311680/rosters');
  const rosters2021 = useAxiosGet('https://api.sleeper.app/v1/league/650025951245193216/rosters');
  const rosters2020 = useAxiosGet('https://api.sleeper.app/v1/league/572103683850252288/rosters');

  const chooseDraftId = (resp) => {
    const arr = Array.isArray(resp.data) ? resp.data : [];
    const complete = arr.find((d) => d.status === 'complete') || arr[0];
    return complete ? complete.draft_id : null;
  };

  const id2026 = chooseDraftId(drafts2026);
  const id2025 = chooseDraftId(drafts2025);
  const id2024 = chooseDraftId(drafts2024);
  const id2023 = chooseDraftId(drafts2023);
  const id2022 = chooseDraftId(drafts2022);
  const id2021 = chooseDraftId(drafts2021);
  const id2020 = chooseDraftId(drafts2020);

  const picks2026 = useAxiosGet(id2026 ? `https://api.sleeper.app/v1/draft/${id2026}/picks` : '');
  const picks2025 = useAxiosGet(id2025 ? `https://api.sleeper.app/v1/draft/${id2025}/picks` : '');
  const picks2024 = useAxiosGet(id2024 ? `https://api.sleeper.app/v1/draft/${id2024}/picks` : '');
  const picks2023 = useAxiosGet(id2023 ? `https://api.sleeper.app/v1/draft/${id2023}/picks` : '');
  const picks2022 = useAxiosGet(id2022 ? `https://api.sleeper.app/v1/draft/${id2022}/picks` : '');
  const picks2021 = useAxiosGet(id2021 ? `https://api.sleeper.app/v1/draft/${id2021}/picks` : '');
  const picks2020 = useAxiosGet(id2020 ? `https://api.sleeper.app/v1/draft/${id2020}/picks` : '');

  const playersResp = useAxiosGet('https://supermegadynasty.com/smd_all_players.json');
  const playerMap = playersResp && playersResp.data && typeof playersResp.data === 'object' ? playersResp.data : null;

  // Build lookups from past_drafts:
  // - pastByUser: `${season}:${round}:${usernameLower}` => [{ name, pick }, ...]
  // - pastByOrig: `${season}:${round}:${originalOwnerLower}` => [{ name, pick }, ...]
  const { pastByUser, pastByOrig } = React.useMemo(() => {
    const byUser = new Map();
    const byOrig = new Map();
    const add = (season, arr) => {
      if (!Array.isArray(arr)) return;
      arr.forEach((it) => {
        if (!it) return;
        const rnd = Number(it.round ?? it.rd ?? it.Round);
        const uname = String(it.username ?? it.user ?? it.owner ?? it.team ?? it.display_name ?? '').trim();
        const orig = String(it.original_owner ?? it.originalOwner ?? it.orig_owner ?? it.owner_original ?? '').trim();
        const name = String(it.player ?? it.player_name ?? it.name ?? '').trim();
        const pick = it.pick != null ? Number(it.pick) : null;
        if (!rnd || !name) return;
        if (uname) {
          const k = `${season}:${rnd}:${uname.toLowerCase()}`;
          if (!byUser.has(k)) byUser.set(k, []);
          byUser.get(k).push({ name, pick });
        }
        if (orig) {
          const k2 = `${season}:${rnd}:${orig.toLowerCase()}`;
          if (!byOrig.has(k2)) byOrig.set(k2, []);
          byOrig.get(k2).push({ name, pick });
        }
      });
    };
    add(2020, past2020.data);
    add(2021, past2021.data);
    add(2022, past2022.data);
    add(2023, past2023.data);
    add(2024, past2024.data);
    add(2025, past2025.data);
    add(2026, past2026.data);
    return { pastByUser: byUser, pastByOrig: byOrig };
  }, [past2020.data, past2021.data, past2022.data, past2023.data, past2024.data, past2025.data, past2026.data]);

  const picksLookup = React.useMemo(() => {
    const map = new Map();
    const addFrom = (season, picks) => {
      const arr = Array.isArray(picks) ? picks : [];
      // Estimate team count from first round
      const teams = new Set(arr.filter(p => p && Number(p.round) === 1).map(p => Number(p.roster_id)).filter(Boolean));
      const teamCount = teams.size || null;
      arr.forEach((p) => {
        if (!p || p.round == null || p.roster_id == null) return;
        const key = `${season}:${Number(p.round)}:${Number(p.roster_id)}`;
        let name = '';
        const md = p.metadata || {};
        if (md.full_name || md.player_name) {
          name = md.full_name || md.player_name;
        } else if (playerMap && p.player_id != null) {
          const pl = playerMap[String(p.player_id)];
          if (pl) name = pl.full_name || `${pl.first_name || ''} ${pl.last_name || ''}`.trim() || pl.name || String(p.player_id);
        }
        const pickNo = p.pick_no || p.pick || null;
        let pickInRound = null;
        if (pickNo && teamCount) {
          pickInRound = ((Number(pickNo) - 1) % teamCount) + 1;
        }
        if (name) map.set(key, { name, round: Number(p.round), pickNo: pickNo ? Number(pickNo) : null, pickInRound });
      });
    };
    addFrom(2026, picks2026.data);
    addFrom(2025, picks2025.data);
    addFrom(2024, picks2024.data);
    addFrom(2023, picks2023.data);
    addFrom(2022, picks2022.data);
    addFrom(2021, picks2021.data);
    addFrom(2020, picks2020.data);
    return map;
  }, [picks2026.data, picks2025.data, picks2024.data, picks2023.data, picks2022.data, picks2021.data, picks2020.data, playerMap]);

  // Build username -> roster_id maps per season by joining users and rosters
  const buildUserToRoster = (usersResp, rostersResp) => {
    const map = new Map();
    const users = Array.isArray(usersResp.data) ? usersResp.data : [];
    const rosters = Array.isArray(rostersResp.data) ? rostersResp.data : [];
    const byUserId = new Map(users.map(u => [String(u.user_id), u]));
    rosters.forEach(r => {
      const u = byUserId.get(String(r.owner_id));
      if (!u) return;
      const uname = String(u.display_name || u.username || '').trim();
      if (uname) map.set(uname, Number(r.roster_id));
    });
    return map;
  };
  const userMapBySeason = {
    2026: buildUserToRoster(users2026, rosters2026),
    2025: buildUserToRoster(users2025, rosters2025),
    2024: buildUserToRoster(users2024, rosters2024),
    2023: buildUserToRoster(users2023, rosters2023),
    2022: buildUserToRoster(users2022, rosters2022),
    2021: buildUserToRoster(users2021, rosters2021),
    2020: buildUserToRoster(users2020, rosters2020),
  };

  const headCells = [
    { id: 'trade_date',          numeric: false, disablePadding: false, label: 'Date', minWidth: 90 },
    // { id: 'trade_partners',      numeric: false, disablePadding: false, label: 'Partners', minWidth: 140 },
    { id: 'trade_team_a',        numeric: false, disablePadding: false, label: 'Team A', minWidth: 120 },
    { id: 'trade_team_a_assets', numeric: false, disablePadding: false, label: 'Team A Adds', minWidth: 260 },
    { id: 'trade_team_b',        numeric: false, disablePadding: false, label: 'Team B', minWidth: 120 },
    { id: 'trade_team_b_assets', numeric: false, disablePadding: false, label: 'Team B Adds', minWidth: 260 },
    { id: 'picks_to_players',    numeric: false, disablePadding: false, label: 'Picks → Player', minWidth: 260 },
  ];

  const rowFilter = React.useCallback((row) => {
    const q = (query || '').trim().toLowerCase();
    if (!q) return true;
    const fields = [
      row.trade_partners,
      row.trade_team_a,
      row.trade_team_b,
      row.trade_team_a_assets,
      row.trade_team_b_assets,
    ];
    return fields.some((v) => String(v || '').toLowerCase().includes(q));
  }, [query]);

  return (
    <div className="md:flex flex-wrap max-w-prose">
      <div className="w-full mb-2">
        <TextField
          size="small"
          label="Filter (partner or asset)"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
        />
      </div>
      <MuiTable
        table="enriched_trades"
        headCells={headCells}
        url={url}
        title="Trade Ledger (All Trades)"
        defaultOrderBy="trade_date"
        defaultOrder="desc"
        page={50}
        rowFilter={rowFilter}
        rowAugment={(row) => {
          const base = (row && typeof row === 'object' && row.last_trade && typeof row.last_trade === 'object') ? row.last_trade : row;
          const lines = [];
          const candidates = [];
          const pushArr = (arr) => { if (Array.isArray(arr)) candidates.push(...arr); };
          // Common arrays directly on the trade object
          pushArr(base && base.draft_picks);
          pushArr(base && base.picks);
          pushArr(base && base.received_picks);
          pushArr(base && base.picks_received);
          // Also check the row level in case enrichment surfaced arrays there
          pushArr(row && row.draft_picks);
          pushArr(row && row.picks);
          pushArr(row && row.received_picks);
          pushArr(row && row.picks_received);
          // Fallback: scan any array-valued fields that look like pick objects
          const scanObj = (obj) => {
            if (!obj || typeof obj !== 'object') return;
            Object.values(obj).forEach((v) => {
              if (Array.isArray(v) && v.length && typeof v[0] === 'object') {
                v.forEach((item) => { if (item && (item.season != null && item.round != null)) candidates.push(item); });
              }
            });
          };
          scanObj(base);
          if (row !== base) scanObj(row);
          // use prebuilt userMapBySeason

          candidates.forEach((pk) => {
            if (!pk || pk.season == null || pk.round == null) return;
            const season = Number(pk.season);
            if (!(season <= 2026 && season >= 2020)) return;
            const rosterHints = [pk.og_owner_id, pk.roster_id, pk.owner_id, pk.to_roster_id, pk.to, pk.recipient, pk.receiver];
            for (const ridRaw of rosterHints) {
              if (ridRaw == null) continue;
              const rid = Number(ridRaw);
              if (!rid) continue;
              const key = `${season}:${Number(pk.round)}:${rid}`;
              const info = picksLookup.get(key);
              if (info) {
                const pr = info.pickInRound ? String(info.pickInRound).padStart(2, '0') : null;
                const roundDisp = pr ? `R${pk.round}.${pr}` : `R${pk.round}`;
                const overall = info.pickNo ? ` (Pick #${info.pickNo})` : '';
                lines.push(`${season} ${roundDisp}${overall} → ${info.name}`);
                break;
              }
            }
          });

          // Fallback: parse username from assets strings like "2021 R3 (caseyott)"
          const gatherStrings = (val) => {
            if (!val) return [];
            if (Array.isArray(val)) return val.map(String);
            return String(val).split(/\n|,\s*/);
          };
          const assetStrs = [
            ...gatherStrings(row.trade_team_a_assets),
            ...gatherStrings(row.trade_team_b_assets),
          ];
          assetStrs.forEach((s) => {
            const m = s.match(/(20\d{2})\s*R\s*\.?\s*(\d+)\s*\(([^)]+)\)/i);
            if (!m) return;
            const season = Number(m[1]);
            const rnd = Number(m[2]);
            // Extract the first token inside parens as username (e.g., "caseyott" from "caseyott via xyz")
            const unameRaw = m[3].trim();
            const uname = unameRaw.split(/[\s,/]+/)[0].toLowerCase();
            if (!(season <= 2026 && season >= 2020)) return;
            // Prefer authoritative mapping: username -> roster_id -> pick selection
            const userToRoster = userMapBySeason[season];
            const rid = userToRoster ? userToRoster.get(unameRaw) || userToRoster.get(uname) : null;
            if (rid != null) {
              const key = `${season}:${rnd}:${Number(rid)}`;
              const info = picksLookup.get(key);
              if (info) {
                const pr = info.pickInRound ? String(info.pickInRound).padStart(2, '0') : null;
                const roundDisp = pr ? `R${rnd}.${pr}` : `R${rnd}`;
                const overall = info.pickNo ? ` (Pick #${info.pickNo})` : '';
                lines.push(`${season} ${roundDisp}${overall} → ${info.name}`);
                return;
              }
            }
            // Fallback: original owner mapping only (avoid user mapping which may include extra acquired picks)
            const entries = pastByOrig.get(`${season}:${rnd}:${uname}`);
            if (Array.isArray(entries) && entries.length) {
              const info = entries[0];
              const roundDisp = `R${rnd}`;
              const overall = info.pick ? ` (Pick #${info.pick})` : '';
              lines.push(`${season} ${roundDisp}${overall} → ${info.name}`);
            }
            // No stub added when mapping is unavailable
          });

          if (lines.length) {
            const uniq = Array.from(new Set(lines));
            row.picks_to_players = uniq.join('\n');
          }
        }}
      />
    </div>
  );
}

function TestTrades() {
  const url = 'https://supermegadynasty.com/smd_trades_enriched.json';

  const headCells = [
    { id: 'trade_date',          numeric: false, disablePadding: false, label: 'Date', minWidth: 90 },
    // { id: 'trade_partners',      numeric: false, disablePadding: false, label: 'Partners', minWidth: 140 },
    { id: 'trade_team_a',        numeric: false, disablePadding: false, label: 'Team A', minWidth: 120 },
    { id: 'trade_team_a_assets', numeric: false, disablePadding: false, label: 'Team A Adds', minWidth: 260 },
    { id: 'trade_team_b',        numeric: false, disablePadding: false, label: 'Team B', minWidth: 120 },
    { id: 'trade_team_b_assets', numeric: false, disablePadding: false, label: 'Team B Adds', minWidth: 260 },
  ];

  return (
    <div className="md:flex flex-wrap max-w-prose w-full">
      <MuiTable
        table="enriched_trades"
        headCells={headCells}
        url={url}
        title="Test: Enriched Trades (Legacy Source)"
        page={50}
      />
    </div>
  );
}

function Most() {
  const url = 'https://supermegadynasty.com/smd_all_most_traded_players.json';

  const headCells = [
    { id: 'trades',     numeric: false, disablePadding: false, label: 'Trades' },
    { id: 'full_name',  numeric: true,  disablePadding: false, label: 'Player' },
    { id: 'last_trade', numeric: true,  disablePadding: false, label: 'Last Trade' },
  ];

  return (
    <div className="md:flex flex-wrap max-w-prose">
      <MuiTable table="most_traded" headCells={headCells} url={url} title="Most Traded Players" page={50} />
    </div>
  );
}
